import { useEffect } from "react";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import HeaderSection from "../../components/HeaderSection";
import { updateUser, getUser } from "../../http";
import Modal from "../../components/modal/Modal";
import { useSelector } from "react-redux";

const EditUser = () => {
  const initialState = {
    // Required fields
    name: "",
    email: "",
    mobile: "",
    password: "",
    type: "",
    address: "",
    profile: "",
    status: "",
    username: "",

    // Education - array of education records (Required)
    education: [
      {
        exam: "",
        stream: "",
        percentage: "",
        passingYear: "",
        institution: "",
      },
    ],

    // Work experience - array of work records (Required)
    workExperience: [
      {
        employer: "",
        designation: "",
        doj: "",
        dol: "",
        tenure: "",
        salary: "",
        reasonForLeaving: "",
      },
    ],

    // Optional fields
    totalExperience: "",
    currentCTC: "",
    expectedCTC: "",
    noticePeriod: "",
    willingToWorkShifts: "Yes",
    dob: "",
    age: "",
    maritalStatus: "Single",

    // Family details
    family: {
      father: { name: "", occupation: "", contact: "" },
      mother: { name: "", occupation: "", contact: "" },
      spouse: { name: "", occupation: "", contact: "" },
      siblings: [{ name: "", occupation: "", contact: "" }],
    },

    // References
    references: [
      {
        name: "",
        position: "",
        organization: "",
        mobile: "",
      },
      {
        name: "",
        position: "",
        organization: "",
        mobile: "",
      },
    ],
  };

  const [imagePreview, setImagePreview] = useState("/assets/icons/user.png");
  const [formData, setFormData] = useState(initialState);
  const [showModal, setShowModal] = useState(false);
  const [updateFormData, setUpdatedFormData] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [userType, setUserType] = useState("User");

  const { id } = useParams();
  const { user } = useSelector((state) => state.authSlice);

  useEffect(() => {
    (async () => {
      const res = await getUser(id, user.type);
      if (res.success) {
        // Initialize all optional fields with default values if they don't exist
        const userData = {
          ...initialState, // First spread the initial state with default values
          ...res.data, // Then spread the API data to override existing values
          // Ensure nested objects and arrays exist with default values if not present
          family: {
            father: { name: "", occupation: "", contact: "" },
            mother: { name: "", occupation: "", contact: "" },
            spouse: { name: "", occupation: "", contact: "" },
            siblings: [{ name: "", occupation: "", contact: "" }],
            ...(res.data.family || {}), // Override with existing family data if present
          },
          education: res.data.education || [
            {
              exam: "",
              stream: "",
              percentage: "",
              passingYear: "",
              institution: "",
            },
          ],
          workExperience: res.data.workExperience || [
            {
              employer: "",
              designation: "",
              doj: "",
              dol: "",
              tenure: "",
              salary: "",
              reasonForLeaving: "",
            },
          ],
          references: res.data.references || [
            {
              name: "",
              position: "",
              organization: "",
              mobile: "",
            },
            {
              name: "",
              position: "",
              organization: "",
              mobile: "",
            },
          ],
          // Ensure other optional fields exist
          totalExperience: res.data.totalExperience || "",
          currentCTC: res.data.currentCTC || "",
          expectedCTC: res.data.expectedCTC || "",
          noticePeriod: res.data.noticePeriod || "",
          willingToWorkShifts: res.data.willingToWorkShifts || "Yes",
          dob: res.data.dob || "",
          age: res.data.age || "",
          maritalStatus: res.data.maritalStatus || "Single",
        };
        setUserType(userData.type);
        setFormData(userData);
        setImagePreview(userData.image || "/assets/icons/user.png");
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  // Calculate age when DOB changes
  useEffect(() => {
    if (formData.dob) {
      const birthDate = new Date(formData.dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setFormData((prev) => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dob]);

  const inputEvent = (e) => {
    const { name, value } = e.target;
    setFormData((old) => ({
      ...old,
      [name]: value,
    }));
    setUpdatedFormData((old) => ({
      ...old,
      [name]: value,
    }));
  };

  // Handle nested object updates (family details)
  const handleFamilyInput = (e, member) => {
    const { name, value } = e.target;
    const updatedFamily = {
      ...formData.family,
      [member]: {
        ...formData.family[member],
        [name]: value,
      },
    };

    setFormData((prev) => ({
      ...prev,
      family: updatedFamily,
    }));

    setUpdatedFormData((prev) => ({
      ...prev,
      family: updatedFamily,
    }));
  };

  // Handle array item updates (siblings, education, work experience, references)
  const handleArrayInput = (e, arrayName, index) => {
    const { name, value } = e.target;
    const newArray = [...formData[arrayName]];
    newArray[index] = { ...newArray[index], [name]: value };

    setFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));

    setUpdatedFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));
  };

  // Add new item to an array
  const addArrayItem = (arrayName, template) => {
    const newArray = [...formData[arrayName], template];

    setFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));

    setUpdatedFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));
  };

  // Remove item from array
  // eslint-disable-next-line
  const removeArrayItem = (arrayName, index) => {
    const newArray = formData[arrayName].filter((_, i) => i !== index);

    setFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));

    setUpdatedFormData((prev) => ({
      ...prev,
      [arrayName]: newArray,
    }));
  };

  // Calculate tenure
  const calculateTenure = (index) => {
    const exp = formData.workExperience[index];
    if (exp.doj && exp.dol) {
      const startDate = new Date(exp.doj);
      const endDate = new Date(exp.dol);

      const months =
        (endDate.getFullYear() - startDate.getFullYear()) * 12 +
        (endDate.getMonth() - startDate.getMonth());

      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;

      const tenure = `${years} year${
        years !== 1 ? "s" : ""
      } ${remainingMonths} month${remainingMonths !== 1 ? "s" : ""}`;

      const updatedWorkExperience = [...formData.workExperience];
      updatedWorkExperience[index] = {
        ...updatedWorkExperience[index],
        tenure,
      };

      setFormData((prev) => ({
        ...prev,
        workExperience: updatedWorkExperience,
      }));

      setUpdatedFormData((prev) => ({
        ...prev,
        workExperience: updatedWorkExperience,
      }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    // if (
    //   !updateFormData.name ||
    //   !updateFormData.email ||
    //   !updateFormData.mobile ||
    //   !updateFormData.type ||
    //   !updateFormData.address
    // ) {
    //   return toast.error(
    //     "Please fill in all required basic information fields"
    //   );
    // }

    // Validate education
    // if (
    //   !updateFormData.education?.length ||
    //   updateFormData.education.some(
    //     (edu) =>
    //       !edu.exam ||
    //       !edu.stream ||
    //       !edu.percentage ||
    //       !edu.passingYear ||
    //       !edu.institution
    //   )
    // ) {
    //   return toast.error("Please fill in all education details");
    // }

    // Validate work experience
    // if (
    //   !updateFormData.workExperience?.length ||
    //   updateFormData.workExperience.some(
    //     (exp) =>
    //       !exp.employer ||
    //       !exp.designation ||
    //       !exp.doj ||
    //       !exp.dol ||
    //       !exp.salary
    //   )
    // ) {
    //   return toast.error("Please fill in all work experience details");
    // }

    if (updateFormData.type && !showModal) {
      setShowModal(true);
      return;
    }

    const fd = new FormData();

    // Add simple key-value pairs
    for (const key in updateFormData) {
      if (typeof updateFormData[key] !== "object") {
        fd.append(key, updateFormData[key]);
      }
    }

    // Add complex objects
    if (updateFormData.family) {
      fd.append("family", JSON.stringify(updateFormData.family));
    }
    if (updateFormData.education) {
      fd.append("education", JSON.stringify(updateFormData.education));
    }
    if (updateFormData.workExperience) {
      fd.append(
        "workExperience",
        JSON.stringify(updateFormData.workExperience)
      );
    }
    // Only add references if they exist and have data
    if (
      updateFormData.references?.some(
        (ref) => ref.name || ref.position || ref.organization || ref.mobile
      )
    ) {
      fd.append("references", JSON.stringify(updateFormData.references));
    }

    const { success, message } = await updateUser(id, fd, user.type);
    if (success) {
      toast.success(message);
      setShowModal(false);
    }
  };

  const captureImage = (e) => {
    const file = e.target.files[0];
    setFormData((old) => {
      return {
        ...old,
        profile: file,
      };
    });

    setUpdatedFormData((old) => {
      return {
        ...old,
        profile: file,
      };
    });
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
  };
  const modalAction = () => setShowModal(showModal ? false : true);

  return (
    <>
      {showModal && (
        <Modal close={modalAction} title="Update User" width="35%">
          <div className="row" style={{ margin: "20px" }}>
            <div className="col col-md-4">
              <div className="input-group justify-content-center text-center">
                <img
                  className="rounded"
                  src={imagePreview}
                  width="120"
                  alt=""
                />
              </div>
            </div>
            <div className="col col-md-8">
              <table className="table table-md">
                <tr>
                  <th>Name</th>
                  <td>{formData.name}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{formData.email}</td>
                </tr>
                <tr>
                  <th>User Type</th>
                  <td>{formData.type}</td>
                </tr>
              </table>
            </div>
          </div>
          <div className="form-group col-md-12">
            <label>Enter Your Password</label>
            <div className="input-group">
              <div className="input-group-prepend">
                <div className="input-group-text">
                  <i className="fas fa-lock"></i>
                </div>
              </div>
              <input
                onChange={inputEvent}
                value={formData.adminPassword}
                type="password"
                placeholder={`Enter Your Password To Change ${formData.name}'s Type`}
                id="adminPassword"
                name="adminPassword"
                className="form-control"
              />
            </div>
          </div>
          <div className="justify-content-center text-center mb-3">
            <button
              className="btn btn-primary btn-lg"
              type="submit"
              form="updateUserForm"
              style={{ width: "30vh" }}>
              Add {formData.type}
            </button>
          </div>
        </Modal>
      )}
      <div className="main-content">
        <section className="section">
          <HeaderSection title={`Edit ${userType}`} />
          <div className="card">
            <div className="card-body pr-5 pl-5 m-1">
              <form className="row" onSubmit={onSubmit} id="updateUserForm">
                <div className="form-group col-md-12 text-center">
                  <div className="input-group justify-content-center">
                    <input
                      type="file"
                      id="profile"
                      name="profile"
                      className="form-control d-none"
                      onChange={captureImage}
                      accept="image/*"
                    />
                    <label htmlFor="profile">
                      {" "}
                      <img
                        className="rounded"
                        src={imagePreview}
                        width="120"
                        alt=""
                      />{" "}
                    </label>
                  </div>
                </div>

                <div className="col-md-12">
                  <h4 className="text-primary">Required Information</h4>
                  <hr />
                </div>

                <div className="form-group col-md-4">
                  <label>Enter Name</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-user"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.name}
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Enter Email</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-envelope"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.email}
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-4">
                  <label>Enter Username</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-user-circle"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.username}
                      type="username"
                      id="username"
                      name="username"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-3">
                  <label>Enter Mobile Number</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-phone"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.mobile}
                      type="tel"
                      id="mobile"
                      name="mobile"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-3">
                  <label>Enter Password</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-lock"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.password}
                      type="password"
                      id="password"
                      name="password"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-3">
                  <label>User Type</label>
                  <select
                    name="type"
                    onChange={inputEvent}
                    value={formData.type}
                    className="form-control select2">
                    <option>Employee</option>
                    <option>Leader</option>
                    <option>Admin</option>
                  </select>
                </div>

                <div className="form-group col-md-3">
                  <label>User Status</label>
                  <select
                    name="status"
                    onChange={inputEvent}
                    value={formData.status}
                    className="form-control select2">
                    <option>Active</option>
                    <option>Banned</option>
                  </select>
                </div>

                <div className="form-group col-md-12 ">
                  <label>Enter Address</label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                    </div>
                    <input
                      onChange={inputEvent}
                      value={formData.address}
                      type="text"
                      id="address"
                      name="address"
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-group col-md-12">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAdvanced(!showAdvanced)}>
                    {showAdvanced ? "Hide" : "Show"} Additional Details
                  </button>
                </div>

                {showAdvanced && (
                  <>
                    <div className="col-md-12">
                      <h4 className="text-primary">Personal Details</h4>
                      <hr />
                    </div>
                    <div className="form-group col-md-3">
                      <label>Enter Date of Birth</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.dob}
                          type="date"
                          id="dob"
                          name="dob"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Enter Age</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-user"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.age}
                          type="text"
                          id="age"
                          name="age"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Enter Marital Status</label>
                      <select
                        name="maritalStatus"
                        onChange={inputEvent}
                        value={formData.maritalStatus}
                        className="form-control select2">
                        <option>Single</option>
                        <option>Married</option>
                        <option>Divorced</option>
                        <option>Widowed</option>
                      </select>
                    </div>

                    <div className="col-md-12">
                      <h4 className="text-primary">Family Background</h4>
                      <hr />
                    </div>
                    <div className="form-group col-md-4">
                      <label>Father's Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-user"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "father")}
                          value={formData.family.father.name}
                          type="text"
                          id="fatherName"
                          name="fatherName"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Father's Occupation</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-briefcase"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "father")}
                          value={formData.family.father.occupation}
                          type="text"
                          id="fatherOccupation"
                          name="fatherOccupation"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Father's Contact</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-phone"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "father")}
                          value={formData.family.father.contact}
                          type="text"
                          id="fatherContact"
                          name="fatherContact"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Mother's Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-user"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "mother")}
                          value={formData.family.mother.name}
                          type="text"
                          id="motherName"
                          name="motherName"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Mother's Occupation</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-briefcase"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "mother")}
                          value={formData.family.mother.occupation}
                          type="text"
                          id="motherOccupation"
                          name="motherOccupation"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Mother's Contact</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-phone"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "mother")}
                          value={formData.family.mother.contact}
                          type="text"
                          id="motherContact"
                          name="motherContact"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Spouse's Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-user"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "spouse")}
                          value={formData.family.spouse.name}
                          type="text"
                          id="spouseName"
                          name="spouseName"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Spouse's Occupation</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-briefcase"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "spouse")}
                          value={formData.family.spouse.occupation}
                          type="text"
                          id="spouseOccupation"
                          name="spouseOccupation"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Spouse's Contact</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-phone"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleFamilyInput(e, "spouse")}
                          value={formData.family.spouse.contact}
                          type="text"
                          id="spouseContact"
                          name="spouseContact"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <h4 className="text-primary">Professional Details</h4>
                      <hr />
                    </div>
                    <div className="form-group col-md-3">
                      <label>Total Experience</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.totalExperience}
                          type="text"
                          id="totalExperience"
                          name="totalExperience"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Current CTC</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-dollar-sign"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.currentCTC}
                          type="text"
                          id="currentCTC"
                          name="currentCTC"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Expected CTC</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-dollar-sign"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.expectedCTC}
                          type="text"
                          id="expectedCTC"
                          name="expectedCTC"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Notice Period</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={inputEvent}
                          value={formData.noticePeriod}
                          type="text"
                          id="noticePeriod"
                          name="noticePeriod"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Willing to Work Shifts</label>
                      <select
                        name="willingToWorkShifts"
                        onChange={inputEvent}
                        value={formData.willingToWorkShifts}
                        className="form-control select2">
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </div>

                    <div className="col-md-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <h4 className="text-primary">
                          Educational Qualifications
                        </h4>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() =>
                            addArrayItem("education", {
                              exam: "",
                              stream: "",
                              percentage: "",
                              passingYear: "",
                              institution: "",
                            })
                          }>
                          Add Education
                        </button>
                      </div>
                      <hr />
                    </div>
                    <div className="form-group col-md-4">
                      <label>Exam</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-graduation-cap"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "education", 0)}
                          value={formData.education[0].exam}
                          type="text"
                          id="educationExam"
                          name="educationExam"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Stream</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-stream"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "education", 0)}
                          value={formData.education[0].stream}
                          type="text"
                          id="educationStream"
                          name="educationStream"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Percentage</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-percent"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "education", 0)}
                          value={formData.education[0].percentage}
                          type="text"
                          id="educationPercentage"
                          name="educationPercentage"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Passing Year</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "education", 0)}
                          value={formData.education[0].passingYear}
                          type="text"
                          id="educationPassingYear"
                          name="educationPassingYear"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Institution</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-university"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "education", 0)}
                          value={formData.education[0].institution}
                          type="text"
                          id="educationInstitution"
                          name="educationInstitution"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <h4 className="text-primary">Work Experience</h4>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() =>
                            addArrayItem("workExperience", {
                              employer: "",
                              designation: "",
                              doj: "",
                              dol: "",
                              tenure: "",
                              salary: "",
                              reasonForLeaving: "",
                            })
                          }>
                          Add Experience
                        </button>
                      </div>
                      <hr />
                    </div>
                    <div className="form-group col-md-4">
                      <label>Employer</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-building"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) =>
                            handleArrayInput(e, "workExperience", 0)
                          }
                          value={formData.workExperience[0].employer}
                          type="text"
                          id="employer"
                          name="employer"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Designation</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-briefcase"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) =>
                            handleArrayInput(e, "workExperience", 0)
                          }
                          value={formData.workExperience[0].designation}
                          type="text"
                          id="designation"
                          name="designation"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Date of Joining</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => {
                            handleArrayInput(e, "workExperience", 0);
                            setTimeout(() => calculateTenure(0), 100);
                          }}
                          value={formData.workExperience[0].doj}
                          type="date"
                          id="doj"
                          name="doj"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-3">
                      <label>Date of Leaving</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => {
                            handleArrayInput(e, "workExperience", 0);
                            setTimeout(() => calculateTenure(0), 100);
                          }}
                          value={formData.workExperience[0].dol}
                          type="date"
                          id="dol"
                          name="dol"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Tenure</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-calendar"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) =>
                            handleArrayInput(e, "workExperience", 0)
                          }
                          value={formData.workExperience[0].tenure}
                          type="text"
                          id="tenure"
                          name="tenure"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Salary</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-dollar-sign"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) =>
                            handleArrayInput(e, "workExperience", 0)
                          }
                          value={formData.workExperience[0].salary}
                          type="text"
                          id="salary"
                          name="salary"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reason for Leaving</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-comment"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) =>
                            handleArrayInput(e, "workExperience", 0)
                          }
                          value={formData.workExperience[0].reasonForLeaving}
                          type="text"
                          id="reasonForLeaving"
                          name="reasonForLeaving"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <h4 className="text-primary">References</h4>
                      <hr />
                    </div>
                    <div className="form-group col-md-4">
                      <label>Reference 1 Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-user"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 0)}
                          value={formData.references[0].name}
                          type="text"
                          id="reference1Name"
                          name="reference1Name"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 1 Position</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-briefcase"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 0)}
                          value={formData.references[0].position}
                          type="text"
                          id="reference1Position"
                          name="reference1Position"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 1 Organization</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-building"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 0)}
                          value={formData.references[0].organization}
                          type="text"
                          id="reference1Organization"
                          name="reference1Organization"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 1 Mobile</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-phone"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 0)}
                          value={formData.references[0].mobile}
                          type="text"
                          id="reference1Mobile"
                          name="reference1Mobile"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 2 Name</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-user"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 1)}
                          value={formData.references[1].name}
                          type="text"
                          id="reference2Name"
                          name="reference2Name"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 2 Position</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-briefcase"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 1)}
                          value={formData.references[1].position}
                          type="text"
                          id="reference2Position"
                          name="reference2Position"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 2 Organization</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-building"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 1)}
                          value={formData.references[1].organization}
                          type="text"
                          id="reference2Organization"
                          name="reference2Organization"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="form-group col-md-4">
                      <label>Reference 2 Mobile</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <div className="input-group-text">
                            <i className="fas fa-phone"></i>
                          </div>
                        </div>
                        <input
                          onChange={(e) => handleArrayInput(e, "references", 1)}
                          value={formData.references[1].mobile}
                          type="text"
                          id="reference2Mobile"
                          name="reference2Mobile"
                          className="form-control"
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="form-group text-center col-md-12">
                  <button
                    className="btn btn-primary btn-lg"
                    type="submit"
                    style={{ width: "30vh" }}>
                    Update {userType}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default EditUser;
